// Б.2.a: Второе сердце — Посмертие носителя.
// В начале следующего хода юнит возвращается на поле с 1 ХП.
//
// Модель:
//   - При смерти носителя ставится флаг в state.pendingResurrects[ownerIdx]:
//       { template, originalLine, originalSlot, deathTurn, name_ru }
//   - В phaseDraw (Ф1) ПОСЛЕ обычной тяги карт обоих игроков applyPendingResurrects
//     создаёт новый Card из шаблона, hp=1, размещает по правилам:
//       1) на ту же ячейку, если свободна;
//       2) иначе на любую свободную в той же линии;
//       3) иначе на любую свободную в другой линии;
//       4) если поле полное — воскрешение пропадает.
//   - Воскрешённый юнит игнорирует Режим ожидания (активен сразу): placedTurn
//     ставится прошлым ходом (currentTurn-1) → !justPlaced → может атаковать
//     Героя; также не считается «свежевыставленным» для Авангарда.
//   - Все Модификации, висевшие на носителе в момент смерти, не возвращаются
//     (юнит «перерождается чистым»).
//   - Сама Модификация считается ИЗРАСХОДОВАННОЙ.

import { Card } from '../../card.js';

export const MOD_ID = 'syn_mod_vtoroe_serdtse';

// Вызывается из onUnitDied для каждой Модификации Второе сердце на погибшем юните.
export function vtoroeSerdtseOnDeath(state, deadOwnerIdx, carrier) {
  if (!state.pendingResurrects) state.pendingResurrects = [[], []];
  state.pendingResurrects[deadOwnerIdx].push({
    template: carrier.template,
    originalLine: carrier.lastLine ?? null,
    originalSlot: carrier.lastSlot ?? null,
    deathTurn: state.currentTurn,
    name_ru: carrier.name_ru
  });
  state.log.add(`❤ ${carrier.name_ru} [${state.players[deadOwnerIdx].name}]: Второе сердце — возврат в Ф1 след. хода`);
}

// Вызывается в phaseDraw ПОСЛЕ обычной тяги карт обоих игроков.
// Воскрешает все pending-юниты, у которых deathTurn < currentTurn.
export function applyPendingResurrects(state) {
  if (!state.pendingResurrects) return;
  for (let idx = 0; idx < 2; idx++) {
    const queue = state.pendingResurrects[idx] || [];
    if (queue.length === 0) continue;
    const remaining = [];
    for (const entry of queue) {
      if (entry.deathTurn >= state.currentTurn) {
        // Слишком рано (умер в этот же ход — например в Ф1 от Перегрузки): отложим.
        remaining.push(entry);
        continue;
      }
      const ok = resurrectOne(state, idx, entry);
      if (!ok) {
        // Поле полностью занято — воскрешение пропадает, без переноса.
      }
    }
    state.pendingResurrects[idx] = remaining;
  }
}

function resurrectOne(state, idx, entry) {
  const p = state.players[idx];
  const slot = pickResurrectSlot(p, entry.originalLine, entry.originalSlot);
  if (!slot) {
    state.log.add(`❤ ${entry.name_ru} [${p.name}]: Второе сердце — поле полно, воскрешение пропадает`);
    return false;
  }
  const card = new Card(entry.template);
  card.ownerId = idx;
  card.current.hp = 1;          // возврат с 1 ХП
  card.alive = true;
  card.revealed = true;
  // Активен сразу: ставим placedTurn в прошлый ход — НЕ свежевыставленный, может бить Героя,
  // не подпадает под "первый ход" в Ф3/Ф4. Игнорирует Режим ожидания: initiation на Card
  // равно template.initiation, isSleeping(currentTurn) сравнивает с initiation, поэтому
  // явно сбросим initiation в 0 на инстансе через сразу же revealed=true и placedTurn<current.
  card.placedTurn = state.currentTurn - 1;
  card.initiation = 0;
  card.modifications = [];
  card.overloaded = false;
  card.stealthUsed = false;
  card.activated = true;
  p.placeCard(card, slot.line, slot.slot);
  state.log.add(`❤ ${entry.name_ru} [${p.name}]: возрождён на ${slot.line === 0 ? '1-ю' : '2-ю'} линию (слот ${slot.slot + 1}) с 1 ХП`);
  // Триггер появления союзника (Бишор/Раст-passive и т. д. на возврат):
  // Раст НЕ должен ставить Мод на возврат (по диздоку только «при появлении»). Однако
  // существующая инфраструктура onUnitSummoned помечает событие; чтобы не плодить багов
  // с двойным Модом, воскрешение из Второго сердца НЕ вызывает onUnitSummoned —
  // юнит «возвращён», а не «призван».
  return true;
}

function pickResurrectSlot(player, origLine, origSlot) {
  // 1) Та же ячейка свободна?
  if (origLine != null && origSlot != null && player.field[origLine][origSlot] === null) {
    return { line: origLine, slot: origSlot };
  }
  // 2) Любая свободная в той же линии.
  if (origLine != null) {
    for (let s = 0; s < 4; s++) {
      if (player.field[origLine][s] === null) return { line: origLine, slot: s };
    }
  }
  // 3) Любая свободная в другой линии.
  const otherLine = origLine != null ? (1 - origLine) : 0;
  for (let s = 0; s < 4; s++) {
    if (player.field[otherLine][s] === null) return { line: otherLine, slot: s };
  }
  // 4) Если orig не задан — обходим обе линии.
  if (origLine == null) {
    for (let l = 0; l < 2; l++) for (let s = 0; s < 4; s++) {
      if (player.field[l][s] === null) return { line: l, slot: s };
    }
  }
  return null;
}
